import { Controller } from '@hotwired/stimulus'
import Rails from '@rails/ujs';

export default class extends Controller {
  static targets = [
    'table',
    'toggle',
    'permissionForm',
    'td',
    'th',
    'tdTemplate',
    'thTemplate',
    'permissionRow',
    'headersRow',
    'addPermissionSelect',
    'saveBtn',
    'addPermissionBtn',
    'selectionModalDismissBtn'
  ]

  connect() {
    this.PERMISSION_TRUE_CLASS = 'has-permission-true'
    this.PERMISSION_FALSE_CLASS = 'has-permission-false'
    this.PERMISSION_DISABLED_CLASS = 'disabled'
    this.PERMISSION_SELECT_ID = 'addPermissionSelect'
    this.AJAX_ERROR_MODAL = 'ajaxErrorModal'
    this.ajaxRequestsInFlight = 0
    this.ajaxDidError = false
  }

  isEditable() {
    return this.tableTarget.hasAttribute('data-editable')
  }

  togglePermission(event) {
    if (!this.isEditable()) {
      return
    }

    const toggleTarget = event.currentTarget
    if (toggleTarget.classList.contains(this.PERMISSION_DISABLED_CLASS)) {
      return
    }

    if (toggleTarget.hasAttribute('data-owner')) {
      this.toggleOwnerTd(toggleTarget)
    } else {
      this.toggleTd(toggleTarget)
    }
    this.saveBtnTargets.forEach(saveBtn => saveBtn.removeAttribute('disabled'))
  }

  toggleTd(element) {
    if (element.classList.contains(this.PERMISSION_TRUE_CLASS)) {
      this.setTdFalse(element)
    } else {
      this.setTdTrue(element)
    }
  }

  toggleOwnerTd(element) {
    const previousState = element.classList.contains(this.PERMISSION_TRUE_CLASS)
    this.toggleTd(element)
    const tdElementsInRow = this.getNonOwnerTdsInRow(element)
    if (previousState === false) {
      tdElementsInRow.forEach(elem => this.setTdDisabled(elem))
      element.parentElement.setAttribute('data-owner', 'owner')
    } else {
      element.parentElement.removeAttribute('data-owner')
      tdElementsInRow.forEach(elem => {
        this.setTdEnabled(elem);
        this.setTdFalse(elem)
      })
    }
  }

  setTdEnabled(element) {
    element.classList.remove(this.PERMISSION_DISABLED_CLASS)
  }

  setTdDisabled(element) {
    element.classList.add(this.PERMISSION_DISABLED_CLASS)
  }

  setTdTrue(element) {
    element.classList.remove(this.PERMISSION_FALSE_CLASS)
    element.classList.add(this.PERMISSION_TRUE_CLASS)
    const formId = element.dataset.permissionFormId
    const value = 'true'
    const attributeName = element.dataset.permissionName
    this.updateForm(formId, attributeName, value)
  }

  setTdFalse(element) {
    element.classList.add(this.PERMISSION_FALSE_CLASS)
    element.classList.remove(this.PERMISSION_TRUE_CLASS)
    const formId = element.dataset.permissionFormId
    const value = 'false'
    const attributeName = element.dataset.permissionName
    this.updateForm(formId, attributeName, value)
  }

  getNonOwnerTdsInRow(element) {
    const htmlCollection = element.parentElement.children
    const tds = Array.from(htmlCollection).filter(child => child.classList.contains('user--permissions') && !child.hasAttribute('data-owner'))
    return tds
  }

  addPermission() {
    if (!this.tdTargets) {
      return
    }

    const select = document.getElementById(this.PERMISSION_SELECT_ID)
    const formId = select.value

    if (!formId || formId.length === 0) {
      return
    }

    const form = this.remoteFormByID(formId)
    form.dataset.dirty = true
    form.dataset.disabled = false
    const selectedTh = this.thByFormId(formId).outerHTML
    this.headersRowTarget.insertAdjacentHTML('beforeend', selectedTh)
    this.permissionRowTargets.forEach(row => {
      const permissionName = row.dataset.permissionName
      const selectedTd = this.tdByFormIdAndPermissionName(formId, permissionName)
      if (row.hasAttribute('data-owner')) {
        selectedTd.classList.add(this.PERMISSION_DISABLED_CLASS)
      }
      row.insertAdjacentHTML('beforeend', selectedTd.outerHTML)
    })
    select.remove(select.selectedIndex)
    if (select.children.length === 1) {
      this.addPermissionBtnTarget.setAttribute('disabled', 'disabled')
      select.setAttribute('disabled', 'disabled')
    }
    this.saveBtnTargets.forEach(saveBtn => saveBtn.removeAttribute('disabled'))
  }

  updateForm(formId, attribute, value) {
    const form = this.remoteFormByID(formId)
    form.dataset.dirty = true
    const inputName = `permission[${attribute}]`
    const theInput = Array.from(form.children).filter(input => input.name === inputName)[0]
    theInput.value = value
  }

  saveAll() {
    this.saveBtnTargets.forEach(saveBtn => saveBtn.setAttribute('disabled', 'disabled'))
    this.permissionFormTargets.filter(form => form.hasAttribute('data-dirty')).forEach(form => {
      this.ajaxRequestsInFlight = this.ajaxRequestsInFlight + 1
      Rails.fire(form, 'submit')
    })
  }

  ajaxError(_event) {
    this.ajaxDidError = true
    this.ajaxRequestsInFlight = this.ajaxRequestsInFlight - 1
    this.processAjaxComplete()
  }

  ajaxSuccess(_event) {
    this.ajaxRequestsInFlight = this.ajaxRequestsInFlight - 1
    this.processAjaxComplete()
  }

  processAjaxComplete() {
    if (this.ajaxRequestsInFlight !== 0) {
      return
    }

    if (this.ajaxDidError === true) {
      this.popAjaxErrorModal()
    } else {
      this.popSuccessFlash()
    }
  }

  popAjaxErrorModal() {
    document.getElementById(this.AJAX_ERROR_MODAL).click()
  }

  popSuccessFlash() {
    const flash = document.getElementById('flashTemplate').innerHTML
    document.getElementById('content').insertAdjacentHTML('afterbegin', flash)
  }

  selectPermissionToAdd() {
    const event = new Event('selectedNewPermission')
    document.dispatchEvent(event)
    this.selectionModalDismissBtnTarget.setAttribute('disabled', 'disabled')
  }

  selectionChanged() {
    const select = document.getElementById(this.PERMISSION_SELECT_ID)
    const formId = select.value

    if (!formId || formId.length === 0) {
      this.selectionModalDismissBtnTarget.setAttribute('disabled', 'disabled')
      return
    }

    this.selectionModalDismissBtnTarget.removeAttribute('disabled')
  }

  remoteFormByID(formId) {
    return this.permissionFormTargets.filter(form => form.id === formId)[0]
  }

  thByFormId(formId) {
    return this.thTargets.filter(th => th.dataset.formId === formId)[0]
  }

  tdByFormIdAndPermissionName(formId, permissionName) {
    return this.tdTargets.filter(td => td.dataset.permissionFormId === formId && td.dataset.permissionName === permissionName)[0]
  }

  trByPermissionName(permissionName) {
    return this.permissionRowTargets.filter(tr => tr.dataset.permissionName === permissionName)[0]
  }

  hideTable(_event) {
    this.tableTarget.classList.add('hidden')
  }

  showTable(_event) {
    this.tableTarget.classList.remove('hidden')
  }
}
